import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, useToast,
} from '@chakra-ui/react';
import {
  limpiezaPanteonesApi, panteonesApi, localidadesApi,
  Localidad, Panteon, LimpiezaPanteon,
} from '../../../api/serviciosApi';
import EvidenciasInline from '../EvidenciasInline';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const ESTATUS = ['Pendiente', 'En proceso', 'Finalizada', 'Cancelada'];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">{children}</Text>
  </Box>
);

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const dateOnly = (v?: string) => (v ? v.split('T')[0] : '');

const FormularioLimpiezaPanteon: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [panteones, setPanteones] = useState<Panteon[]>([]);
  const [guardando, setGuardando] = useState(false);

  const [folio, setFolio] = useState(data?.folio || '');
  const [panteonId, setPanteonId] = useState(data?.panteonId != null ? String(data.panteonId) : '');
  const [fecha, setFecha] = useState(dateOnly(data?.fecha) || new Date().toISOString().split('T')[0]);
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [actividades, setActividades] = useState(data?.actividades || '');
  const [horaInicio, setHoraInicio] = useState(data?.horaInicio || '');
  const [horaTermino, setHoraTermino] = useState(data?.horaTermino || '');
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [localidad, setLocalidad] = useState(data?.localidad || '');
  const [estatus, setEstatus] = useState(data?.estatus || 'Pendiente');

  useEffect(() => {
    localidadesApi.list().then(setLocalidades).catch(() => setLocalidades([]));
    panteonesApi.list().then(setPanteones).catch(() => setPanteones([]));
  }, []);

  const handleGuardar = async () => {
    if (!panteonId || !fecha) {
      toast({ title: 'Campos obligatorios', description: 'El panteón y la fecha son requeridos.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const loc = localidades.find((l) => l.descripcion === localidad);
      const body: Partial<LimpiezaPanteon> = {
        folio: folio || undefined, panteonId: Number(panteonId), fecha, responsable,
        actividades, horaInicio, horaTermino, observaciones,
        localidad: localidad || undefined, localidadId: loc?.id, estatus,
      };
      if (modo === 'edit') await limpiezaPanteonesApi.update(data.id, body);
      else await limpiezaPanteonesApi.create(body);
      toast({ title: modo === 'edit' ? 'Registro actualizado' : 'Registro creado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar el registro.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const fmt = (v?: string) => (v ? new Date(v).toLocaleDateString('es-MX') : '—');

  if (modo === 'view') {
    const d = data || {};
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos del Registro</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="FOLIO" value={d.folio} />
          <Field label="PANTEÓN" value={d.panteon?.nombre} />
          <Field label="FECHA" value={fmt(d.fecha)} />
          <Field label="RESPONSABLE" value={d.responsable} />
          <Field label="HORARIO" value={d.horaInicio ? `${d.horaInicio} — ${d.horaTermino || '?'}` : undefined} />
          <Field label="LOCALIDAD" value={d.localidad} />
          <Field label="ESTATUS" value={<Badge>{d.estatus}</Badge>} />
        </SimpleGrid>
        {d.actividades && (<><SectionTitle>Actividades</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.actividades}</Text></>)}
        {d.observaciones && (<><SectionTitle>Observaciones</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.observaciones}</Text></>)}
        <EvidenciasInline archivos={d.archivos} />
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos del Registro</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl><FormLabel fontSize="sm">Folio</FormLabel><Input value={folio} onChange={(e) => setFolio(e.target.value)} /></FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Panteón</FormLabel>
          <Select placeholder="Selecciona panteón" value={panteonId} onChange={(e) => setPanteonId(e.target.value)}>
            {panteones.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl isRequired><FormLabel fontSize="sm">Fecha</FormLabel><Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Responsable</FormLabel><Input value={responsable} onChange={(e) => setResponsable(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Hora inicio</FormLabel><Input type="time" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Hora término</FormLabel><Input type="time" value={horaTermino} onChange={(e) => setHoraTermino(e.target.value)} /></FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona localidad" value={localidad} onChange={(e) => setLocalidad(e.target.value)}>
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            {ESTATUS.map((e) => <option key={e} value={e}>{e}</option>)}
          </Select>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}><FormLabel fontSize="sm">Actividades</FormLabel><Textarea rows={3} value={actividades} onChange={(e) => setActividades(e.target.value)} /></FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}><FormLabel fontSize="sm">Observaciones</FormLabel><Textarea rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></FormControl>
      </SimpleGrid>
      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">Guardar</Button>
      </HStack>
    </VStack>
  );
};

export default FormularioLimpiezaPanteon;
